
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Chip,
    Button,
    CircularProgress,
    Stack,
    ListItem,
    ListItemIcon,
    ListItemText,
    alpha,
    Select,
    MenuItem
} from '@mui/material';
import {
    Download,
    Verified,
    Info,
    CheckCircle,
    Visibility
} from '@mui/icons-material';
import React from 'react';

// ─── Premium Card Container ──────────────────────────────────────────────────
export const StyledCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{
            borderRadius: '16px',
            border: '1px solid rgba(117, 34, 209, 0.08)',
            overflow: 'hidden',
            bgcolor: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(117, 34, 209, 0.04)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
                boxShadow: '0 12px 32px rgba(117, 34, 209, 0.08)',
                transform: 'translateY(-2px)',
                borderColor: 'rgba(117,34,209,0.15)',
            },
            ...sx
        }}
    >
        {children}
    </Paper>
);

// ─── Card Section Header ──────────────────────────────────────────────────────
export const CardHeader = ({ title, icon, action }) => (
    <Box sx={{
        px: 3,
        py: 2.5,
        borderBottom: '1px solid rgba(117,34,209,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        bgcolor: 'rgba(117,34,209,0.015)',
    }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                bgcolor: '#F5EEFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                {icon}
            </Box>
            <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#1E293B', letterSpacing: '-0.01em' }}>
                {title}
            </Typography>
        </Stack>
        {action && (
            <Box>
                {action}
            </Box>
        )}
    </Box>
);

// ─── Contact Info List Item ───────────────────────────────────────────────────
export const InfoItem = ({ label, value, icon }) => (
    <ListItem sx={{ py: 1.5, px: 0, borderBottom: '1px solid rgba(117,34,209,0.04)', '&:last-child': { borderBottom: 'none' } }}>
        <ListItemIcon sx={{ minWidth: 40 }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '10px',
                bgcolor: '#F5EEFC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#7522D1',
            }}>
                {icon}
            </Box>
        </ListItemIcon>
        <ListItemText
            primary={label}
            secondary={value || 'Not provided'}
            primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' } }}
            secondaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 700, color: '#1E293B', mt: 0.2 } }}
        />
    </ListItem>
);

// ─── Detail Block (2-col personal info grids) ─────────────────────────────────
export const DetailBlock = ({ label, value, icon, color }) => (
    <Box sx={{
        p: 1.5,
        borderRadius: '10px',
        bgcolor: '#FAFAFA',
        border: '1px solid rgba(117,34,209,0.05)',
        mb: 0.5,
    }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.4 }}>
            {icon && <Box sx={{ color: '#9CA3AF', display: 'flex' }}>{icon}</Box>}
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.06em' }}>{label}</Typography>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 800, color: color || '#1E293B' }}>{value || '—'}</Typography>
    </Box>
);

// ─── Identity / Document Verification Row ────────────────────────────────────
export const IdentityVerificationRow = ({ label, value, personName, status, onStatusChange, verifying, documents, onDownloadClick, onViewClick }) => {
    const statusColor = status === 'Verified' ? '#10B981' : (status === 'Rejected' ? '#EF4444' : '#F59E0B');
    const statusBg = status === 'Verified' ? 'rgba(16,185,129,0.08)' : (status === 'Rejected' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)');
    return (
        <Box sx={{
            p: 2.5,
            borderRadius: '14px',
            border: '1px solid',
            borderColor: status === 'Verified' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)',
            bgcolor: statusBg,
            transition: 'all 0.2s ease',
        }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', mb: 0.5, letterSpacing: '0.06em', fontSize: '0.65rem' }}>{label}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#1E293B', mb: personName ? 0.5 : 1, letterSpacing: '0.04em', fontFamily: 'monospace' }}>{value || '—'}</Typography>
                    {personName && <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', mb: 1 }}>{personName}</Typography>}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor }} />
                        <Typography variant="caption" sx={{ fontWeight: 800, color: statusColor }}>{status || 'Pending'}</Typography>
                    </Box>

                    {documents && documents.some(d => d.url) && (
                        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 0.5 }}>
                            {documents.filter(d => d.url).map((doc, idx) => (
                                <React.Fragment key={idx}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Visibility sx={{ fontSize: '12px !important' }} />}
                                        {...(onViewClick ? {
                                            onClick: () => onViewClick(doc.url)
                                        } : {
                                            component: "a",
                                            href: doc.url,
                                            target: "_blank"
                                        })}
                                        sx={{
                                            borderRadius: '8px',
                                            fontSize: '0.68rem',
                                            py: 0.4,
                                            px: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderColor: 'rgba(117,34,209,0.2)',
                                            color: '#7522D1',
                                            bgcolor: '#FFFFFF',
                                            '&:hover': { borderColor: '#7522D1', bgcolor: '#F5EEFC' }
                                        }}
                                    >
                                        View {doc.label}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Download sx={{ fontSize: '12px !important' }} />}
                                        {...(onDownloadClick ? {
                                            onClick: () => onDownloadClick(doc.url, doc.label, label)
                                        } : {
                                            component: "a",
                                            href: doc.url,
                                            target: "_blank",
                                            download: true
                                        })}
                                        sx={{
                                            borderRadius: '8px',
                                            fontSize: '0.68rem',
                                            py: 0.4,
                                            px: 1.5,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderColor: 'rgba(117,34,209,0.2)',
                                            color: '#7522D1',
                                            bgcolor: '#FFFFFF',
                                            '&:hover': { borderColor: '#7522D1', bgcolor: '#F5EEFC' }
                                        }}
                                    >
                                        {doc.label}
                                    </Button>
                                </React.Fragment>
                            ))}
                        </Stack>
                    )}
                </Box>
                {status !== 'Verified' && onStatusChange && (
                    <Select
                        size="small"
                        value=""
                        displayEmpty
                        onChange={(e) => onStatusChange(e.target.value)}
                        disabled={verifying}
                        sx={{
                            borderRadius: '10px',
                            height: 36,
                            fontWeight: 800,
                            bgcolor: '#10B981',
                            color: 'white',
                            ml: 2,
                            boxShadow: '0 4px 12px rgba(16,185,129,0.2)',
                            '&:hover': { boxShadow: '0 8px 16px rgba(16,185,129,0.3)', bgcolor: '#059669' },
                            '& .MuiSelect-icon': { color: 'white' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { py: 0.5, px: 2 }
                        }}
                        renderValue={(value) => {
                            if (verifying) return <CircularProgress size={16} color="inherit" />;
                            return "Action";
                        }}
                    >
                        <MenuItem value="Verified" sx={{ color: 'success.main', fontWeight: 700 }}>Verify</MenuItem>
                        <MenuItem value="Rejected" sx={{ color: 'error.main', fontWeight: 700 }}>Reject</MenuItem>
                    </Select>
                )}
            </Stack>
        </Box>
    );
};

// ─── Label / Value pair ───────────────────────────────────────────────────────
export const LabelValue = ({ label, value, boldValue, color, icon, sx }) => (
    <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={sx}>
        {icon && <Box sx={{ color: '#9CA3AF', mt: 0.3, display: 'flex' }}>{icon}</Box>}
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#9CA3AF', display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.62rem' }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: boldValue ? 800 : 700, color: color || '#1E293B', mt: 0.2, lineHeight: 1.4 }}>
                {value || '—'}
            </Typography>
        </Box>
    </Stack>
);

// ─── Onboarding Status Tick ───────────────────────────────────────────────────
export const StatusTick = ({ label, completed }) => (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{
        p: 1.5,
        borderRadius: '10px',
        bgcolor: completed ? 'rgba(16,185,129,0.06)' : '#FAFAFA',
        border: '1px solid',
        borderColor: completed ? 'rgba(16,185,129,0.15)' : 'rgba(0,0,0,0.04)',
        transition: 'all 0.2s',
    }}>
        <Box sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: completed ? '#10B981' : '#E2E8F0',
            flexShrink: 0,
        }}>
            <CheckCircle sx={{ fontSize: 16, color: completed ? '#FFFFFF' : '#94A3B8' }} />
        </Box>
        <Typography variant="body2" sx={{ color: completed ? '#065F46' : '#94A3B8', fontWeight: completed ? 700 : 600, fontSize: '0.875rem' }}>{label}</Typography>
        {completed && (
            <Chip label="Done" size="small" sx={{ ml: 'auto', height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: 'rgba(16,185,129,0.12)', color: '#059669', borderRadius: '6px' }} />
        )}
    </Stack>
);

// ─── Address Display ──────────────────────────────────────────────────────────
export const AddressDisplay = ({ address }) => {
    if (!address) return (
        <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: 600 }}>No address on record</Typography>
        </Box>
    );
    return (
        <Stack spacing={0.8}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', lineHeight: 1.5 }}>
                {address.houseNo ? `${address.houseNo}, ` : ''} {address.houseName || ''}
            </Typography>
            {address.street && <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>{address.street}</Typography>}
            {address.landmark && <Typography variant="caption" sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>near {address.landmark}</Typography>}
            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>{address.city}, {address.state}</Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#7522D1' }}>{address.country}</Typography>
                <Typography variant="caption" sx={{ bgcolor: '#F5EEFC', color: '#7522D1', px: 1, py: 0.2, borderRadius: '6px', fontWeight: 700 }}>{address.pincode}</Typography>
            </Box>
        </Stack>
    );
};

// ─── Timeline Item (Education / Experience) ───────────────────────────────────
export const TimelineItem = ({ title, subtitle, period, description, documents, isLast, icon, color, onDownloadClick, onViewClick }) => (
    <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={{
                width: 36,
                height: 36,
                bgcolor: alpha(color, 0.1),
                color: color,
                zIndex: 1,
                border: `2px solid ${alpha(color, 0.2)}`,
            }}>
                {icon}
            </Avatar>
            {!isLast && <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(117,34,209,0.08)', my: 1, borderRadius: '2px' }} />}
        </Box>
        <Box sx={{ pb: isLast ? 0 : 4, pt: 0.5, flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E293B' }}>{title}</Typography>
                <Typography variant="caption" sx={{ bgcolor: '#F5EEFC', color: '#7522D1', px: 1.5, py: 0.3, borderRadius: '20px', fontWeight: 700, fontSize: '0.7rem' }}>{period}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 700, color: color, mb: 0.5 }}>{subtitle}</Typography>
            {description && <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.85rem', mb: 1, lineHeight: 1.6 }}>{description}</Typography>}
            {documents && documents.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, gap: 0.5 }}>
                    {documents.filter(doc => doc.url).map((doc, idx) => (
                        <React.Fragment key={idx}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Visibility sx={{ fontSize: '12px !important' }} />}
                                {...(onViewClick ? {
                                    onClick: () => onViewClick(doc.url)
                                } : {
                                    component: "a",
                                    href: doc.url,
                                    target: "_blank"
                                })}
                                sx={{
                                    borderRadius: '8px',
                                    fontSize: '0.68rem',
                                    py: 0.3,
                                    px: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderColor: alpha(color, 0.25),
                                    color: color,
                                    '&:hover': { borderColor: color, bgcolor: alpha(color, 0.05) }
                                }}
                            >
                                View {doc.label}
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Download sx={{ fontSize: '12px !important' }} />}
                                {...(onDownloadClick ? {
                                    onClick: () => onDownloadClick(doc.url, doc.label, title)
                                } : {
                                    component: "a",
                                    href: doc.url,
                                    target: "_blank"
                                })}
                                sx={{
                                    borderRadius: '8px',
                                    fontSize: '0.68rem',
                                    py: 0.3,
                                    px: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    borderColor: alpha(color, 0.25),
                                    color: color,
                                    '&:hover': { borderColor: color, bgcolor: alpha(color, 0.05) }
                                }}
                            >
                                {doc.label}
                            </Button>
                        </React.Fragment>
                    ))}
                </Stack>
            )}
        </Box>
    </Box>
);

// ─── Identity Block ───────────────────────────────────────────────────────────
export const IdentityBlock = ({ label, value, verified }) => (
    <Box sx={{
        p: 2.5,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: verified ? 'rgba(16,185,129,0.2)' : 'rgba(117,34,209,0.08)',
        bgcolor: verified ? 'rgba(16,185,129,0.03)' : '#FAFAFA',
    }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>{label}</Typography>
            {verified && (
                <Chip
                    label="Verified"
                    size="small"
                    icon={<Verified sx={{ fontSize: '12px !important', color: '#059669 !important' }} />}
                    sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800, bgcolor: 'rgba(16,185,129,0.1)', color: '#059669', borderRadius: '6px' }}
                />
            )}
        </Stack>
        <Typography variant="body1" sx={{ fontWeight: 800, color: '#1E293B', letterSpacing: '0.08em', fontFamily: 'monospace', fontSize: '1rem' }}>{value || 'NOT PROVIDED'}</Typography>
    </Box>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
export const EmptyState = ({ message }) => (
    <Box sx={{ py: 6, textAlign: 'center' }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#F5EEFC', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <Info sx={{ fontSize: 28, color: '#C4B5E0' }} />
        </Box>
        <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: 700 }}>{message}</Typography>
    </Box>
);